/**
 * Config Loader for brick.json
 * Loads and validates brick configuration from project root
 */

import fs from "fs-extra";
import path from "path";
import pc from "picocolors";
import type { DeepRequired } from "./types/utils";

export interface BrickConfig {
  output?: {
    typescript?: string;
    ios?: string;
    android?: string;
    androidAppPath?: string;
  };
}

export class BrickConfigLoader {
  private static readonly CONFIG_FILE_NAME = "brick.json";

  private static readonly DEFAULT_CONFIG: DeepRequired<BrickConfig> = {
    output: {
      typescript: ".brick",
      ios: "ios/.brick",
      android: "android/.brick",
      androidAppPath: "android/app",
    },
  };

  /**
   * Load brick.json configuration from project root
   */
  static load(projectRoot: string): BrickConfig {
    const configPath = path.join(
      projectRoot,
      BrickConfigLoader.CONFIG_FILE_NAME,
    );

    // If no config file exists, return empty config (will use defaults)
    if (!fs.existsSync(configPath)) {
      BrickConfigLoader.log("No brick.json found, using default paths");
      return {};
    }

    try {
      // Read and parse config file
      const configContent = fs.readFileSync(configPath, "utf-8");
      const config = JSON.parse(configContent) as BrickConfig;

      // Validate configuration
      BrickConfigLoader.validateConfig(config, projectRoot);

      // Convert relative paths to absolute paths for internal use
      const normalizedConfig = BrickConfigLoader.normalizeConfig(
        config,
        projectRoot,
      );

      BrickConfigLoader.log(
        `Loaded configuration from ${BrickConfigLoader.CONFIG_FILE_NAME}`,
      );
      return normalizedConfig;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(
          `Invalid JSON in ${BrickConfigLoader.CONFIG_FILE_NAME}: ${error.message}`,
        );
      }
      throw error;
    }
  }

  /**
   * Get default configuration
   */
  static getDefaults(): DeepRequired<BrickConfig> {
    return { ...BrickConfigLoader.DEFAULT_CONFIG };
  }

  /**
   * Merge configuration with defaults
   */
  static mergeWithDefaults(config: BrickConfig): DeepRequired<BrickConfig> {
    const defaults = BrickConfigLoader.getDefaults();

    return {
      output: {
        typescript: config.output?.typescript || defaults.output.typescript,
        ios: config.output?.ios || defaults.output.ios,
        android: config.output?.android || defaults.output.android,
        androidAppPath:
          config.output?.androidAppPath || defaults.output.androidAppPath,
      },
    };
  }

  /**
   * Validate configuration
   */
  private static validateConfig(
    config: BrickConfig,
    _projectRoot: string,
  ): void {
    // Validate output paths if provided
    if (config.output) {
      const { typescript, ios, android, androidAppPath } = config.output;

      // Check for absolute paths (not allowed)
      if (typescript && path.isAbsolute(typescript)) {
        throw new Error(
          `output.typescript must be a relative path, got: ${typescript}`,
        );
      }
      if (ios && path.isAbsolute(ios)) {
        throw new Error(`output.ios must be a relative path, got: ${ios}`);
      }
      if (android && path.isAbsolute(android)) {
        throw new Error(
          `output.android must be a relative path, got: ${android}`,
        );
      }
      if (androidAppPath && path.isAbsolute(androidAppPath)) {
        throw new Error(
          `output.androidAppPath must be a relative path, got: ${androidAppPath}`,
        );
      }

      // Check for dangerous paths
      const dangerousPaths = [".", "..", "/", "~"];
      if (typescript && dangerousPaths.includes(typescript)) {
        throw new Error(`output.typescript cannot be '${typescript}'`);
      }
      if (ios && dangerousPaths.includes(ios)) {
        throw new Error(`output.ios cannot be '${ios}'`);
      }
      if (android && dangerousPaths.includes(android)) {
        throw new Error(`output.android cannot be '${android}'`);
      }
      if (androidAppPath && dangerousPaths.includes(androidAppPath)) {
        throw new Error(`output.androidAppPath cannot be '${androidAppPath}'`);
      }

      // Warn if paths go outside project root
      if (typescript?.startsWith("..")) {
        BrickConfigLoader.warn(
          `output.typescript goes outside project root: ${typescript}`,
        );
      }
      if (ios?.startsWith("..")) {
        BrickConfigLoader.warn(`output.ios goes outside project root: ${ios}`);
      }
      if (android?.startsWith("..")) {
        BrickConfigLoader.warn(
          `output.android goes outside project root: ${android}`,
        );
      }
      if (androidAppPath?.startsWith("..")) {
        BrickConfigLoader.warn(
          `output.androidAppPath goes outside project root: ${androidAppPath}`,
        );
      }
    }
  }

  /**
   * Normalize configuration paths
   * Keep paths relative for external use, but validate they're valid
   */
  private static normalizeConfig(
    config: BrickConfig,
    _projectRoot: string,
  ): BrickConfig {
    if (!config.output) {
      return config;
    }

    // Return config as-is but validated
    return {
      ...config,
      output: config.output
        ? {
            typescript: config.output.typescript,
            ios: config.output.ios,
            android: config.output.android,
            androidAppPath: config.output.androidAppPath,
          }
        : undefined,
    };
  }

  /**
   * Get absolute path from config path
   */
  static getAbsolutePath(
    configPath: string | undefined,
    projectRoot: string,
    defaultPath: string,
  ): string {
    const relativePath = configPath || defaultPath;
    return path.join(projectRoot, relativePath);
  }

  private static log(message: string): void {
    console.log(`${pc.bold(pc.cyan("[Brick]"))} ${message}`);
  }

  private static warn(message: string): void {
    console.warn(`${pc.bold(pc.yellow("[Brick]"))} Warning: ${message}`);
  }
}
